/**
 * Унифицированный компонент кнопок действий для таблиц
 * Дата: 30.01.2026
 * 
 * Используется в:
 * - Users.tsx — редактирование/удаление пользователей
 * - Templates.tsx — редактирование/удаление шаблонов
 * - Services.tsx — редактирование/удаление услуг
 * - AutoReplies.tsx — редактирование/удаление правил
 * - Admin.tsx — действия с тенантами
 * 
 * Связан с: components/ui/Button.tsx, components/ui/index.ts
 */
import { cn } from '@/utils/cn';
import { Button } from './Button';
import { Edit2, Trash2, Eye, Copy, Settings, Power } from 'lucide-react';

export interface ActionButtonsProps {
  /** Callback для редактирования */
  onEdit?: () => void;
  /** Callback для удаления */
  onDelete?: () => void;
  /** Callback для просмотра */
  onView?: () => void;
  /** Callback для копирования */
  onCopy?: () => void;
  /** Callback для настроек */
  onSettings?: () => void;
  /** Callback для вкл/выкл */
  onToggle?: () => void;
  /** Состояние toggle (для иконки) */
  isActive?: boolean;
  /** Размер кнопок */
  size?: 'sm' | 'md';
  /** Направление: row или column */
  direction?: 'row' | 'column';
  /** Показывать подписи */
  showLabels?: boolean;
  /** Дополнительные классы */
  className?: string;
  /** testId префикс */
  testIdPrefix?: string;
}

/**
 * ActionButtons — унифицированные кнопки действий для таблиц
 * 
 * @example
 * // Базовое использование (Edit + Delete)
 * <ActionButtons
 *   onEdit={() => openEditModal(item)}
 *   onDelete={() => handleDelete(item.id)}
 * />
 * 
 * @example
 * // Все кнопки
 * <ActionButtons
 *   onView={() => viewItem(item)}
 *   onEdit={() => editItem(item)}
 *   onCopy={() => copyItem(item)}
 *   onDelete={() => deleteItem(item)}
 *   size="sm"
 * />
 */
export function ActionButtons({
  onEdit,
  onDelete,
  onView,
  onCopy,
  onSettings,
  onToggle,
  isActive,
  size = 'sm',
  direction = 'row',
  showLabels = false,
  className,
  testIdPrefix,
}: ActionButtonsProps) {
  const buttons = [
    { key: 'view', icon: <Eye className="w-4 h-4" />, label: 'Просмотр', onClick: onView },
    { key: 'edit', icon: <Edit2 className="w-4 h-4" />, label: 'Редактировать', onClick: onEdit },
    { key: 'copy', icon: <Copy className="w-4 h-4" />, label: 'Копировать', onClick: onCopy },
    { key: 'settings', icon: <Settings className="w-4 h-4" />, label: 'Настройки', onClick: onSettings },
    { key: 'toggle', icon: <Power className={cn('w-4 h-4', isActive ? 'text-green-500' : 'text-red-500')} />, label: isActive ? 'Выключить' : 'Включить', onClick: onToggle },
    { key: 'delete', icon: <Trash2 className="w-4 h-4 text-red-500" />, label: 'Удалить', onClick: onDelete, variant: 'ghost' as const },
  ].filter(btn => btn.onClick);

  if (buttons.length === 0) return null;

  return (
    <div 
      className={cn(
        'flex gap-1',
        direction === 'column' && 'flex-col',
        className
      )}
    >
      {buttons.map(({ key, icon, label, onClick, variant }) => (
        <Button
          key={key}
          variant={variant || 'ghost'}
          size={size}
          onClick={onClick}
          title={label}
          data-testid={testIdPrefix ? `${testIdPrefix}-${key}` : undefined}
        >
          {icon}
          {showLabels && <span className="ml-1">{label}</span>}
        </Button>
      ))}
    </div>
  );
}
